// Créneaux horaires d'une soirée : normalisation du passage à minuit, et test de recouvrement.
//
// Partagé entre la modale « Booker les extras » et le serveur. Un salarié ne peut pas tenir deux créneaux
// qui se recouvrent — règle d'Emeni (2026-08-28) : « je peux mettre Emeni sur l'horaire de 18h à 00h, et
// le deuxième horaire de 18h à 02h je ne peux pas ; par contre 18h à 20h puis 20h à 3h, oui ». Le masquage
// d'une pastille côté écran ne sécurise rien : le serveur applique la même règle, et il doit l'appliquer
// avec le MÊME calcul, sinon les deux finissent par diverger au premier ajustement.

/** "18:30" -> 1110. Rend NaN pour une valeur vide ou mal formée, ce que les appelants traitent comme absent. */
const enMinutes = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || '').trim());
    if (!m) return NaN;
    return Number(m[1]) * 60 + Number(m[2]);
};

/**
 * Un créneau ramené à deux nombres de minutes sur une ligne de temps continue.
 *
 * Une soirée franchit minuit : « 18:00 → 02:00 » se termine le LENDEMAIN. Comparés bruts, 18:00 → 02:00 et
 * 20:00 → 02:00 paraîtraient l'un vide et l'autre inversé, et aucun recouvrement ne serait detecté. La fin
 * est donc reportée d'un jour dès qu'elle n'est pas strictement postérieure au début.
 */
export const normaliserCreneau = (start, end) => {
    const debut = enMinutes(start);
    let fin = enMinutes(end);
    if (Number.isNaN(debut)) return null;
    if (Number.isNaN(fin)) return { debut, fin: debut };
    if (fin <= debut) fin += 24 * 60;
    return { debut, fin };
};

// Au-delà de cet écart entre deux heures de début, on considère que les créneaux ne sont pas sur la même
// demi-journée et que le plus matinal appartient au lendemain. Douze heures : deux créneaux d'une même
// soirée ne s'écartent jamais autant, alors qu'un « 01:00 » face à un « 19:00 » est forcément la nuit qui
// suit. Ancrer plutôt sur l'heure de l'événement serait faux — un poste peut commencer AVANT la soirée
// (hôtesses à 18:00 pour une privatisation qui ouvre à 19:00), et le créneau se retrouvait décalé d'un jour.
const ECART_JOUR = 12 * 60;

/**
 * Vrai si les deux créneaux se recouvrent réellement. Deux créneaux qui se touchent bout à bout
 * (20:00 → 00:00 puis 00:00 → 02:00) ne se recouvrent PAS : c'est un enchaînement, et Emeni veut pouvoir
 * y placer la même personne. D'où les comparaisons strictes.
 */
export const creneauxSeChevauchent = (a, b) => {
    const x = normaliserCreneau(a && a.start, a && a.end);
    let y = normaliserCreneau(b && b.start, b && b.end);
    // Un créneau sans horaire exploitable ne bloque personne : mieux vaut laisser passer que refuser un
    // booking legitime sur une donnee incomplete.
    if (!x || !y) return false;
    // Les deux créneaux sont replacés sur une même nuit avant d'être comparés : « 01:00 → 05:00 » suit
    // « 19:00 → 02:00 », il ne le précède pas de dix-huit heures.
    let a2 = x;
    let b2 = y;
    if (b2.debut - a2.debut > ECART_JOUR) a2 = { debut: a2.debut + 24 * 60, fin: a2.fin + 24 * 60 };
    else if (a2.debut - b2.debut > ECART_JOUR) b2 = { debut: b2.debut + 24 * 60, fin: b2.fin + 24 * 60 };
    return a2.debut < b2.fin && b2.debut < a2.fin;
};

/** "18:00 → 02:00", pour dire à l'écran POURQUOI une personne est indisponible plutôt que la griser sans mot. */
export const libelleCreneau = (start, end) => {
    const d = String(start || '').slice(0, 5);
    const f = String(end || '').slice(0, 5);
    if (!d) return '';
    return f ? `${d} → ${f}` : d;
};
